/**
 * Printable/PDF rendering of the Profit & Loss account cards report.
 * Sections, row order and subtotals come straight from the model, so the PDF matches
 * both the on-screen report and the Profit & Loss report of LedgerReport.
 */

import { beautifyNumber } from '../../../Utils/FormatNumber';

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const styles = `
  * { box-sizing: border-box; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    margin: 0;
    padding: 18px;
    color: #000;
    font-size: 11px;
  }
  /* A section is often taller than a page, so it must be allowed to flow across pages.
     Forcing "page-break-inside: avoid" here left the first page blank. Rows and the
     section heading stay with their content instead, and headers repeat per page. */
  .report-header { text-align: center; margin-bottom: 16px; page-break-after: avoid; break-after: avoid; }
  .report-title { font-size: 17px; font-weight: 700; }
  .report-meta { font-size: 11px; margin-top: 4px; }
  .section { margin-bottom: 16px; page-break-inside: auto; break-inside: auto; }
  .section-header {
    border: 1px solid #000;
    padding: 4px 8px;
    margin-bottom: 4px;
    page-break-after: avoid;
    break-after: avoid;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .section-ag-label { font-size: 13px; font-weight: 700; }
  .section-detail-label { font-size: 11px; font-weight: 700; }
  table { width: 100%; border-collapse: collapse; page-break-inside: auto; break-inside: auto; }
  thead { display: table-header-group; }
  tfoot { display: table-footer-group; }
  tr { page-break-inside: avoid; break-inside: avoid; }
  /* The transactions row wraps a whole nested table and can exceed a page on its own,
     so it opts back out of the row-level "keep together" rule. */
  .detail-row, .detail-row > td, .detail-block { page-break-inside: auto; break-inside: auto; }
  th, td { padding: 3px 6px; }
  thead th { border-bottom: 1px solid #000; font-size: 10px; text-align: right; }
  .amount { text-align: left; direction: ltr; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .palcode-row td { font-weight: 700; border-bottom: 1px solid #ccc; padding-top: 6px; }
  .card-row td { border-bottom: 1px dotted #ddd; }
  .subtotal td { font-weight: 700; border-top: 1px solid #000; }
  .category-total td { font-weight: 700; border-top: 1px solid #000; }
  .group-total td { font-weight: 700; border-top: 3px double #000; }
  .running-total td { font-weight: 700; }
  .grand-total td { font-weight: 700; border-top: 3px double #000; border-bottom: 3px double #000; font-size: 12px; }
  .detail-block { padding: 2px 0 8px 18px; }
  .detail-title { font-size: 10px; font-weight: 700; margin-bottom: 2px; }
  .detail-table th, .detail-table td { font-size: 9px; padding: 2px 4px; }
  .detail-table thead th { border-bottom: 1px solid #999; }
  .detail-table tbody td { border-bottom: 1px dotted #e0e0e0; }
  .detail-total td { font-weight: 700; border-top: 1px solid #999; }
`;

const detailColumns = (t) => [
  { key: 'creditAccountName', label: t('Credit Account') },
  { key: 'debitAccountName', label: t('Debit Account') },
  { key: 'debitAccount', label: t('Debit'), amount: true },
  { key: 'creditAccount', label: t('Credit'), amount: true },
  { key: 'balance', label: t('Balance'), amount: true },
  { key: 'uploadDate', label: t('ValueDate'), date: true },
  { key: 'documentDate', label: t('ReferenceDate'), date: true },
  { key: 'reference1', label: t('Reference1') },
  { key: 'reference2', label: t('Reference2') },
  // Descriptions are stored as translation keys for system-generated rows.
  { key: 'description', label: t('Details'), translate: true },
];

export const buildProfitAndLossCardsReportHtml = ({
  model,
  title,
  clientName,
  clientCase,
  period,
  showPercentage,
  showAccountCodeId,
  showTransactions,
  formatDate,
  t,
}) => {
  const columnsCount = 2 + (showPercentage ? 1 : 0);
  const details = detailColumns(t);

  const cardLabel = (card) => {
    const name = card.accountCardName || '';
    return showAccountCodeId && card.accountCardId
      ? `${card.accountCardId} ${name}`.trim()
      : name;
  };

  const amountCells = (value, percentage) => `
    <td class="amount">${beautifyNumber(value)}</td>
    ${showPercentage ? `<td class="amount">${escapeHtml(percentage)}</td>` : ''}
  `;

  const renderDetailTable = (card) => {
    if (!showTransactions || !card.transactions.length) return '';

    const rows = card.transactions.map((transaction) => `
      <tr>
        ${details.map((column) => {
          const value = transaction[column.key];
          if (column.amount) return `<td class="amount">${beautifyNumber(value)}</td>`;
          if (column.date) return `<td>${value ? escapeHtml(formatDate(value)) : '-'}</td>`;
          if (!value) return '<td>-</td>';
          return `<td>${escapeHtml(column.translate ? t(value, { defaultValue: value }) : value)}</td>`;
        }).join('')}
      </tr>
    `).join('');

    return `
      <tr class="detail-row">
        <td colspan="${columnsCount}">
          <div class="detail-block">
            <div class="detail-title">
              ${escapeHtml(t('Transactions'))} (${card.transactions.length})
            </div>
            <table class="detail-table">
              <thead>
                <tr>${details.map((column) => `<th>${escapeHtml(column.label)}</th>`).join('')}</tr>
              </thead>
              <tbody>
                ${rows}
                <tr class="detail-total">
                  <td colspan="2">${escapeHtml(t('Total'))}</td>
                  <td class="amount">${beautifyNumber(card.transactions.reduce((sum, tr) => sum + (Number(tr.debitAccount) || 0), 0))}</td>
                  <td class="amount">${beautifyNumber(card.transactions.reduce((sum, tr) => sum + (Number(tr.creditAccount) || 0), 0))}</td>
                  <td class="amount">${beautifyNumber(card.transactionsTotal)}</td>
                  <td colspan="${details.length - 5}"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </td>
      </tr>
    `;
  };

  const sectionsHtml = model.sections.map((section) => {
    const bodyRows = section.palCodes.map((palCode) => `
      <tr class="palcode-row">
        <td colspan="${columnsCount}">${escapeHtml(palCode.palCodeLabel)}</td>
      </tr>
      ${palCode.cards.map((card) => `
        <tr class="card-row">
          <td>${escapeHtml(cardLabel(card))}</td>
          ${amountCells(card.balance, card.percentage)}
        </tr>
        ${renderDetailTable(card)}
      `).join('')}
      <tr class="subtotal">
        <td>${escapeHtml(`${t('Total')}: ${palCode.palCodeLabel}`)}</td>
        ${amountCells(palCode.total, palCode.percentage)}
      </tr>
    `).join('');

    const categoryTotalRow = `
      <tr class="category-total">
        <td>${escapeHtml(`${t('Total')}: ${section.categoryLabel}`)}</td>
        ${amountCells(section.categoryTotal, section.categoryPercentage)}
      </tr>
    `;

    const groupTotalRow = section.isLastCategoryInGroup ? `
      <tr class="group-total">
        <td>${escapeHtml(`${t('Total')} ${section.accountingGroupName}:`)}</td>
        ${amountCells(section.groupTotal, section.groupPercentage)}
      </tr>
    ` : '';

    const runningTotalRow = section.showRunningTotal ? `
      <tr class="running-total">
        <td>${escapeHtml(t('ProfitAndLossAfterGroup', { group: section.accountingGroupName }))}</td>
        ${amountCells(section.runningTotal, section.runningPercentage)}
      </tr>
    ` : '';

    return `
      <div class="section">
        <div class="section-header">
          ${section.showAgLabel ? `<div class="section-ag-label">${escapeHtml(section.agLabel)}</div>` : ''}
          <div class="section-detail-label">${escapeHtml(section.detailLabel)}</div>
        </div>
        <table>
          <thead>
            <tr>
              <th>${escapeHtml(t('AccountCard'))}</th>
              <th class="amount">${escapeHtml(t('Balance'))}</th>
              ${showPercentage ? `<th class="amount">${escapeHtml(t('PercentOfSales'))}</th>` : ''}
            </tr>
          </thead>
          <tbody>
            ${bodyRows}
            ${categoryTotalRow}
            ${groupTotalRow}
            ${runningTotalRow}
          </tbody>
        </table>
      </div>
    `;
  }).join('');

  return `<!DOCTYPE html>
<html dir="rtl" lang="he">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>${styles}</style>
</head>
<body dir="rtl">
  <div class="report-header">
    <div class="report-title">${escapeHtml(title)}</div>
    <div class="report-meta">${escapeHtml(clientName || '')}${clientCase ? ` | ${escapeHtml(clientCase)}` : ''}</div>
    <div class="report-meta">${escapeHtml(period || '')}</div>
  </div>
  ${sectionsHtml}
  <table>
    <tbody>
      <tr class="grand-total">
        <td>${escapeHtml(t('TotalProfitAndLossInReport'))}</td>
        <td class="amount">${beautifyNumber(model.grandTotal)}</td>
        ${showPercentage ? `<td class="amount">${escapeHtml(model.grandTotalPercentage)}</td>` : ''}
      </tr>
    </tbody>
  </table>
</body>
</html>`;
};

export default buildProfitAndLossCardsReportHtml;
